import { Box, Button, MenuItem, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { journalClient } from '../../api/client';
import { QueryJournalEvent } from '../../api/schema';
import { CopyTsvButton } from '../../component/copyTsvButton/CopyTsvButton';
import { DatePicker } from '../../component/datePicker/DatePicker';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getJournalEvents, journalEventsFetching, listJournal } from '../../redux/slices/journal/journalSlice';
import { listOrganizations, selectAllOrganizations } from '../../redux/slices/organizations/organizationsSlice';
import { isAdminUserSelector } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { Card } from '../../styled/components';
import { ENTITY_TYPES, entityLabel, relatedHref, rowHref } from './journalLinks';

// Rows the copy fetches at most; the API caps a page at this too.
const EXPORT_LIMIT = 5000;
const URL_FORMAT = "yyyy-MM-dd'T'HH:mm";

const when = (iso?: string | null): string => (iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_SHORT) : '');
const toUrl = (d: Date): string => DateTime.fromJSDate(d).toFormat(URL_FORMAT);
const fromUrl = (s?: string): Date | null => {
  if (!s) {
    return null;
  }
  const d = DateTime.fromFormat(s, URL_FORMAT);
  return d.isValid ? d.toJSDate() : null;
};
// The API takes RFC3339 in UTC; the pickers work in the viewer's local time.
const toApi = (d: Date | null): string | undefined => (d ? DateTime.fromJSDate(d).toUTC().toISO({ suppressMilliseconds: true }) ?? undefined : undefined);

// A record's name as a link when it still exists, plain text with a hint
// otherwise. A delivery has no page of its own, so it links through its site
// visit (delivered) and otherwise reads as text.
const RecordCell = ({ name, href, exists, fallback }: { name?: string; href?: string; exists?: boolean; fallback: string }): ReactElement => {
  const text = name || fallback;
  if (href) {
    return (
      <MuiLink component={Link} to={href}>
        {text}
      </MuiLink>
    );
  }
  return (
    <span>
      {text}
      {!exists && name ? (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          (deleted)
        </Typography>
      ) : null}
    </span>
  );
};

// Your Company → Journal: every data-changing action the organization's
// users took. Filters live in the URL so a view can be shared; the window
// defaults to the last day.
export const Journal = (): ReactElement => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const isAdmin = useSelector(isAdminUserSelector);

  // Window: From/To from the URL. With neither, the pickers show the last day
  // and the request sends no bounds, so the API applies "last 24 hours" at
  // request time and a refresh always shows the newest rows.
  const defaultWindow = useMemo(() => {
    const now = new Date();
    return { begin: new Date(now.getTime() - 24 * 60 * 60 * 1000), end: now };
  }, []);
  const hasWindow = Boolean(params.occurredBegin || params.occurredEnd);
  // Memoized on the URL strings: a fresh Date per render would change the
  // query identity and refetch on every render.
  const begin = useMemo(() => (hasWindow ? fromUrl(params.occurredBegin) : defaultWindow.begin), [hasWindow, params.occurredBegin, defaultWindow]);
  const end = useMemo(() => (hasWindow ? fromUrl(params.occurredEnd) : defaultWindow.end), [hasWindow, params.occurredEnd, defaultWindow]);

  // Free-text fields are local until the debounce lands them in the URL.
  const [user, setUser] = useState(params.user ?? '');
  const [search, setSearch] = useState(params.search ?? '');
  useEffect(() => setUser(params.user ?? ''), [params.user]);
  useEffect(() => setSearch(params.search ?? ''), [params.search]);
  const pushText = useMemo(
    () =>
      debounce((key: 'user' | 'search', value: string) => {
        setURLParams({ newParams: { [key]: value.trim(), offset: '' } });
      }, 400),
    [setURLParams],
  );
  useEffect(() => () => pushText.cancel(), [pushText]);

  // TAG admins may narrow to one organization.
  const orgQuery = useMemo(() => ({ active: 'true', offset: 0, limit: 10000, full: 'yes' }), []);
  const organizations = useSelector(selectAllOrganizations(orgQuery));
  useEffect(() => {
    if (isAdmin) {
      dispatch(listOrganizations(orgQuery));
    }
  }, [dispatch, isAdmin, orgQuery]);
  const orgOptions = useMemo(() => [...(organizations.result ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')), [organizations.result]);

  const listQuery = useMemo(
    () => ({
      ...(params.entityType ? { entityType: params.entityType } : {}),
      ...(params.entityId ? { entityId: params.entityId } : {}),
      ...(params.user ? { user: params.user } : {}),
      ...(params.search ? { search: params.search } : {}),
      ...(params.organizationId && isAdmin ? { organizationId: params.organizationId } : {}),
      ...(hasWindow ? { occurredBegin: toApi(begin), occurredEnd: toApi(end) } : {}),
    }),
    [params.entityType, params.entityId, params.user, params.search, params.organizationId, isAdmin, hasWindow, begin, end],
  );
  const events = useSelector(getJournalEvents(listQuery));
  const fetching = useSelector(journalEventsFetching);
  const [pagination, paginationToGet, paginationControl] = useTablePagination({ paginationDirty: events.pagination, defaultLimit: 50 });

  useEffect(() => {
    dispatch(listJournal({ ...listQuery, ...paginationToGet }));
  }, [dispatch, listQuery, paginationToGet]);

  const setWindow = useCallback(
    (which: 'occurredBegin' | 'occurredEnd', value: Date | null | '') => {
      if (value === '' || (value && isNaN(value.getTime()))) {
        return;
      }
      const other = which === 'occurredBegin' ? end : begin;
      setURLParams({
        newParams: {
          [which]: value ? toUrl(value) : '',
          [which === 'occurredBegin' ? 'occurredEnd' : 'occurredBegin']: other ? toUrl(other) : '',
          offset: '',
        },
      });
    },
    [begin, end, setURLParams],
  );
  const resetWindow = useCallback(() => setURLParams({ newParams: { occurredBegin: '', occurredEnd: '', offset: '' } }), [setURLParams]);

  const columns = useMemo(
    () => [
      { Header: 'When', accessor: 'occurred_at', sortBy: 'occurred_at', Cell: ({ value }: { value?: string }) => when(value), minWidth: 150 },
      {
        Header: 'User',
        accessor: 'user_name',
        sortBy: 'user_name',
        Cell: ({ row }: { row: { original: QueryJournalEvent } }) => row.original.user_name || row.original.user_email || '',
      },
      ...(isAdmin ? [{ Header: 'Organization', accessor: 'organization_name' }] : []),
      { Header: 'Action', accessor: 'label', sortBy: 'label' },
      {
        Header: 'Affected',
        accessor: 'entity_name',
        sortBy: 'entity_name',
        Cell: ({ row }: { row: { original: QueryJournalEvent } }) => (
          <RecordCell
            name={row.original.entity_name}
            href={rowHref(row.original, isAdmin)}
            exists={row.original.entity_exists}
            fallback={entityLabel(row.original.entity_type)}
          />
        ),
      },
      { Header: 'Type', accessor: 'entity_type', sortBy: 'entity_type', Cell: ({ value }: { value?: string }) => entityLabel(value) },
      {
        Header: 'Related',
        accessor: 'related_name',
        Cell: ({ row }: { row: { original: QueryJournalEvent } }) =>
          row.original.related_type ? (
            <RecordCell
              name={row.original.related_name}
              href={relatedHref(row.original, isAdmin)}
              exists={row.original.related_exists}
              fallback={entityLabel(row.original.related_type)}
            />
          ) : (
            ''
          ),
      },
      { Header: 'Details', accessor: 'summary', ellipsis: true, minWidth: 220 },
    ],
    [isAdmin],
  );

  const tsvRow = (e: QueryJournalEvent) => [
    when(e.occurred_at),
    e.user_name || e.user_email,
    e.label,
    e.entity_name,
    entityLabel(e.entity_type),
    e.related_name,
    e.summary,
    e.source,
  ];
  const fetchAll = useCallback(async () => {
    const res = await journalClient.listJournal(
      listQuery.entityType,
      listQuery.entityId,
      undefined,
      listQuery.user,
      listQuery.search,
      listQuery.occurredBegin,
      listQuery.occurredEnd,
      listQuery.organizationId,
      pagination.sortBy || undefined,
      0,
      EXPORT_LIMIT,
    );
    return (res.data.result ?? []).map(tsvRow);
  }, [listQuery, pagination.sortBy]);

  return (
    <>
      {fetching && <LinearProgress />}
      <Card>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Affected"
              id="journal-entity-type"
              value={params.entityType ?? ''}
              onChange={(e) => setURLParams({ newParams: { entityType: e.target.value, offset: '' } })}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All records</MenuItem>
              {ENTITY_TYPES.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="User"
              id="journal-user"
              placeholder="name or email"
              value={user}
              onChange={(e) => {
                setUser(e.target.value);
                pushText('user', e.target.value);
              }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Search"
              id="journal-search"
              placeholder="site, tool, action…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                pushText('search', e.target.value);
              }}
              sx={{ minWidth: 220 }}
            />
            <Box sx={{ width: 210 }}>
              <DatePicker label="From" type="dateTime" inputFormat="MM/dd/yyyy HH:mm" value={begin} onChange={(v) => setWindow('occurredBegin', v)} />
            </Box>
            <Box sx={{ width: 210 }}>
              <DatePicker label="To" type="dateTime" inputFormat="MM/dd/yyyy HH:mm" value={end} onChange={(v) => setWindow('occurredEnd', v)} />
            </Box>
            {hasWindow && (
              <Button size="small" onClick={resetWindow}>
                Last day
              </Button>
            )}
            {isAdmin && (
              <TextField
                select
                size="small"
                label="Organization"
                id="journal-organization"
                value={params.organizationId ?? ''}
                onChange={(e) => setURLParams({ newParams: { organizationId: e.target.value, offset: '' } })}
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="">All organizations</MenuItem>
                {orgOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <CopyTsvButton label="Journal" header={['When', 'User', 'Action', 'Affected', 'Type', 'Related', 'Details', 'Source']} getRows={fetchAll} />
          </Stack>
          <Table columns={columns} data={events.result ?? []} size="small" pagination={pagination} paginationControl={paginationControl} />
        </Box>
      </Card>
    </>
  );
};

export default Journal;
